import { create } from 'zustand';
import { AdriStatus, PendingAction, RouteState, getAdriStatus, getHandoff, getRoute } from '../services/adriApi';

/** Acción opcional que acompaña a un mensaje local de Adri. */
export type LocalMessageAction = 'next-step';

export interface AdriLocalMessage {
  id: number;
  text: string;
  action?: LocalMessageAction;
  createdAt: string;
}

export interface AdriState {
  isOpen: boolean;
  /** true mientras corre una guía: el panel se oculta por completo sin cerrar el chat. */
  collapsed: boolean;
  status: AdriStatus | null;
  route: RouteState | null;
  pendingAction: PendingAction | null;
  localMessages: AdriLocalMessage[];
  unreadCount: number;
  open: () => void;
  close: () => void;
  toggle: () => void;
  setCollapsed: (collapsed: boolean) => void;
  loadStatus: (force?: boolean) => Promise<AdriStatus | null>;
  loadRoute: () => Promise<RouteState | null>;
  setRoute: (route: RouteState | null) => void;
  setPendingAction: (action: PendingAction | null) => void;
  pushLocalMessage: (text: string, action?: LocalMessageAction) => void;
  clearLocalMessages: () => void;
  setUnreadCount: (count: number) => void;
  /** Spec 052: una vez por sesión de navegador, pinta el saludo de traspaso si existe. */
  loadHandoff: () => Promise<void>;
}

let statusRequest: Promise<AdriStatus | null> | null = null;
let localSeq = 0;
let handoffRequested = false;

/** Solo para pruebas: reinicia el flag de sesión del traspaso. */
export const __resetHandoffFlag = () => {
  handoffRequested = false;
};

/**
 * 051 — Estado único del copiloto Adri (apertura del chat, guía en curso,
 * estado del copiloto y ruta de activación). `useSupportChatStore` lo reexporta
 * para que el Centro de ayuda y el widget compartan la misma fuente de verdad.
 */
export const useAdriStore = create<AdriState>((set, get) => ({
  isOpen: false,
  collapsed: false,
  status: null,
  route: null,
  pendingAction: null,
  localMessages: [],
  unreadCount: 0,

  open: () => set({ isOpen: true, collapsed: false }),
  close: () => set({ isOpen: false, collapsed: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen, collapsed: false })),
  setCollapsed: (collapsed) => set({ collapsed }),

  loadStatus: async (force = false) => {
    if (!force && get().status) return get().status;
    if (!force && statusRequest) return statusRequest;
    statusRequest = getAdriStatus()
      .then((status) => {
        set({ status });
        return status;
      })
      .catch(() => {
        set({ status: null });
        return null;
      })
      .finally(() => {
        statusRequest = null;
      });
    return statusRequest;
  },

  loadRoute: async () => {
    if (!get().status?.copilot) return get().route;
    try {
      const route = await getRoute();
      set({ route: route ?? null });
      return route ?? null;
    } catch {
      // 403 ADRI_DISABLED u error de red: se conserva la última ruta conocida.
      return get().route;
    }
  },

  setRoute: (route) => set({ route }),
  setPendingAction: (pendingAction) => set({ pendingAction }),

  pushLocalMessage: (text, action) =>
    set((s) => ({
      localMessages: [
        ...s.localMessages,
        { id: -(Date.now() * 10 + (localSeq++ % 10)), text, action, createdAt: new Date().toISOString() },
      ],
    })),
  clearLocalMessages: () => set({ localMessages: [] }),
  setUnreadCount: (unreadCount) => set({ unreadCount: Math.max(0, unreadCount) }),

  loadHandoff: async () => {
    if (handoffRequested) return;
    handoffRequested = true;
    try {
      const { greeting } = await getHandoff();
      if (greeting) get().pushLocalMessage(greeting);
    } catch {
      // Sin saludo: el cajón queda como siempre.
    }
  },
}));
